import type { i18n as I18n } from "i18next";

/**
 * "Never show a translation key" — the helpers behind it.
 *
 * The locale files (public/locales/<lng>/{translation,errors}.json, ~0.5 MB each) are
 * fetched at runtime. Until they land, every t("some.key") returns the key itself, so
 * anything rendered in that window shows raw keys. Two moments are affected:
 *   - first load: handled by <I18nGate>, which holds the app until isI18nReady();
 *   - a language switch: handled by installSafeLanguageSwitch(), which makes sure the
 *     new language is actually in memory before i18next switches to it.
 */

const namespacesOf = (i18n: I18n): string[] => {
  const ns = i18n.options?.ns;
  if (!ns) return ["translation"];
  return Array.isArray(ns) ? ns : [ns];
};

/** The language plus its fallbacks, e.g. "he" → ["he", "en"]. */
const hierarchyOf = (i18n: I18n, lng: string): string[] =>
  i18n.services?.languageUtils?.toResolveHierarchy(lng) ?? [lng];

/** Languages in `lng`'s hierarchy that still miss at least one namespace. */
export const missingLanguages = (i18n: I18n, lng: string): string[] =>
  hierarchyOf(i18n, lng).filter((l) => namespacesOf(i18n).some((ns) => !i18n.hasResourceBundle(l, ns)));

/** True once the active language and its fallback are in memory, for every namespace. */
export const isI18nReady = (i18n: I18n): boolean => {
  if (!i18n.isInitialized || !i18n.language) return false;
  if (i18n.language.toLowerCase() === "cimode") return true;
  return missingLanguages(i18n, i18n.language).length === 0;
};

/**
 * (Re)fetches `lngs`. i18next remembers a failed file (state -1) and then skips it for
 * good — even on reloadResources — so a phone that was offline once would never get that
 * language until a full page reload. Clearing the remembered failure first makes a retry
 * really go to the network. (i18next itself already retries network errors a few times
 * inside one attempt.)
 */
export const fetchLanguages = (i18n: I18n, lngs: string[]): Promise<void> =>
  new Promise((resolve) => {
    const state: Record<string, number> | undefined = (i18n.services?.backendConnector as any)?.state;
    if (state) {
      lngs.forEach((l) => namespacesOf(i18n).forEach((ns) => {
        if (state[`${l}|${ns}`] === -1) delete state[`${l}|${ns}`];
      }));
    }
    void i18n.reloadResources(lngs, namespacesOf(i18n), () => resolve());
  });

/**
 * Makes i18n.changeLanguage() switch only once the new language is loaded.
 *
 * i18next 23 already waits for the backend before it flips `i18n.language` — but if
 * the request FAILS it switches anyway, to a language with no strings, and the whole
 * app shows keys. Here the files are fetched first (one retry), the switch happens only
 * when they are in memory, and otherwise the current language simply stays. When two
 * switches overlap, only the last one is applied.
 *
 * Every caller (old and new pages) uses i18n.changeLanguage directly, so the guard is
 * installed on the instance rather than asking each call site to use a helper.
 */
export const installSafeLanguageSwitch = (i18n: I18n): void => {
  const marked = i18n as I18n & { __safeLanguageSwitch?: boolean };
  if (marked.__safeLanguageSwitch) return;
  marked.__safeLanguageSwitch = true;

  const original = i18n.changeLanguage.bind(i18n);
  let latest: string | undefined;

  const safe = async (lng?: string, callback?: (err: unknown, t: I18n["t"]) => void) => {
    // i18next's own init() calls changeLanguage before isInitialized — leave that alone.
    if (!i18n.isInitialized || !lng) return original(lng, callback);

    latest = lng;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const missing = missingLanguages(i18n, lng);
      if (!missing.length) break;
      await fetchLanguages(i18n, missing);
    }

    const t = i18n.t.bind(i18n) as I18n["t"];
    if (latest !== lng) {
      // A later switch was requested meanwhile; that one decides.
      callback?.(null, t);
      return t;
    }
    if (missingLanguages(i18n, lng).length) {
      console.warn(`[i18n] Could not load "${lng}" — staying in "${i18n.language}".`);
      callback?.(new Error(`Could not load language ${lng}`), t);
      return t;
    }
    return original(lng, callback);
  };

  i18n.changeLanguage = safe as I18n["changeLanguage"];
};

/**
 * <html dir/lang> for a language (RTL for Hebrew). App keeps them in sync on later switches;
 * the gate calls this before the first paint so a Hebrew page never shows one LTR frame.
 */
export const applyDocumentLanguage = (lng?: string): void => {
  const isRtl = (lng || "").toLowerCase().startsWith("he");
  document.documentElement.dir = isRtl ? "rtl" : "ltr";
  document.documentElement.lang = isRtl ? "he" : (lng || "en");
};

/**
 * Options for i18next-http-backend. `version` (a hash of the locale files, set at build
 * time) is added as ?v=…, so each deploy's texts have their own URL: nginx can cache
 * them for a year, and a browser can never pair a new bundle with old texts.
 */
export const backendOptions = (version?: string) => ({
  loadPath: "/locales/{{lng}}/{{ns}}.json",
  queryStringParams: version ? { v: version } : {},
});
